//@ts-strict-ignore
import React, { useMemo, useState } from 'react';

import CustomerContact, {
  CustomerContactLabel,
  CustomerContactLetter,
  CustomerContactMailshot,
} from '../../graphql/entities/customer/CustomerContact';
import customerContactAttributeQuery from 'graphql/queries/customer/CustomerContactAttributeConfigListQuery.graphql';
import customerAttributeQuery from 'graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import { usePredefinedFilters } from '../../getPredefinedFilters';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import ModalContext from '../../components/Context/ModalContext';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import fieldMetaData, { IEnumMetaData } from '../../graphql/schema/fieldMetaData';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { HiddenTextFormatter } from '../../components/grid-addons/formatters/HiddenTextFormatter';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import Workspace from '../../components/Workspace';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { FilterOperation, ICustomerContact, ReportType } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { EntityType } from '../../@types/entityTypes';
import { Maybe } from 'graphql/jsutils/Maybe';

const { workspace, type, title } = CustomerContact;

const CONTACT_DEFAULT_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  telephoneNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowPhoneCalls,
    },
  },
  mobileNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowPhoneCalls,
    },
  },
  faxNumber: {
    formatter: HiddenTextFormatter,
    options: {
      condition: (rowData) => rowData?.allowFax,
    },
  },
  allowMarketing: {
    name: 'Allow Email Marketing',
  },
  allowPostalMail: {
    name: 'Allow Direct Marketing',
  },
  'address.line1': {
    name: 'Address Line 1',
  },
  'address.line2': {
    name: 'Address Line 2',
  },
  'address.line3': {
    name: 'Address Line 3',
  },
  'address.line4': {
    name: 'Address Line 4',
  },
  'address.line5': {
    name: 'Address Line 5',
  },
  'address.postcode': {
    name: 'Postcode',
  },
  'address.country.name': {
    name: 'Country',
  },
  'customerGlobalContact.address.line1': {
    name: 'Global Contact - Address Line 1',
  },
  'customerGlobalContact.address.line2': {
    name: 'Global Contact - Address Line 2',
  },
  'customerGlobalContact.address.line3': {
    name: 'Global Contact - Address Line 3',
  },
  'customerGlobalContact.address.line4': {
    name: 'Global Contact - Address Line 4',
  },
  'customerGlobalContact.address.line5': {
    name: 'Global Contact - Address Line 5',
  },
  'customerGlobalContact.address.postcode': {
    name: 'Global Contact - Postcode',
  },
  'customerGlobalContact.address.country.name': {
    name: 'Global Contact - Country',
  },
  'customer.priceSource.name': {
    name: 'Customer - Price Source',
  },
};

const CUSTOMER_CONTACT_FIELD_NAMES: Array<Paths<ICustomerContact>> = [
  'name.fullName',
  'address.line1',
  'email',
  'allowMarketing',
];

export const ContactDocumentModal = ({
  modalOpen,
  setModalOpen,
  entity,
  customerContact = null,
  multiValue = true,
}: {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entity: EntityType;
  customerContact?: Maybe<ICustomerContact>;
  multiValue?: boolean;
}) => {
  return (
    <ModalContext.Provider
      value={{
        onClose: () => {
          setModalOpen(false);
        },
      }}
    >
      <DocumentModal2
        entity={entity}
        modalOpen={modalOpen}
        returnUrl={null}
        title={entity.configuration?.title}
        modalWrapper={true}
        multiValue={multiValue}
        type={multiValue ? 'customerContacts' : 'customerContact'}
        additionalInputVariables={{
          reportType: entity.configuration?.reportType,
        }}
        allowEmail={true}
        allowEmailTo={false}
        customerContact={customerContact}
        outputSelection={
          entity.configuration?.reportType === ReportType.CustomerContactLabel
            ? (fieldMetaData.DocumentOutputType as IEnumMetaData).enumValues.filter((en) => en.value !== 'EMAIL')
            : undefined
        }
      />
    </ModalContext.Provider>
  );
};

const CustomerContactWorkspaceActions = (props) => {
  const [letterModalOpen, setLetterModalOpen] = useState(false);
  const [mailshotModalOpen, setMailshotModalOpen] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);

  const customerContactWorkspaceActions = [
    {
      key: 'customerContactLetter',
      onClick: () => {
        setLetterModalOpen(true);
      },
      text: 'Contact Letter',
      actionReference: 'CUSTOMER_CONTACT_LETTER',
    },
    {
      key: 'customerContactMailshot',
      onClick: () => {
        setMailshotModalOpen(true);
      },
      text: 'Contact Mailshot',
      actionReference: 'CUSTOMER_CONTACT_MAILSHOT',
    },
    {
      key: 'customerContactLabel',
      onClick: () => {
        setLabelModalOpen(true);
      },
      text: 'Contact Label',
      actionReference: 'CUSTOMER_CONTACT_LABEL',
    },
  ];

  return (
    <>
      <ContactDocumentModal
        modalOpen={letterModalOpen}
        setModalOpen={setLetterModalOpen}
        entity={CustomerContactLetter}
      />
      <ContactDocumentModal
        modalOpen={mailshotModalOpen}
        setModalOpen={setMailshotModalOpen}
        entity={CustomerContactMailshot}
      />
      <ContactDocumentModal modalOpen={labelModalOpen} setModalOpen={setLabelModalOpen} entity={CustomerContactLabel} />
      <WorkspaceActionButtons {...props} actionButtons={customerContactWorkspaceActions} />
    </>
  );
};

const CustomerContactWorkspace = (props) => {
  const predefinedFilters = usePredefinedFilters(props);

  const attributeColumns = useAttributesForWorkspace(customerContactAttributeQuery);
  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(() => {
    return { ...(props.customColumns || {}), ...attributeColumns, ...customerAttributeColumns };
  }, [props.customColumns, attributeColumns, customerAttributeColumns]);

  const workspaceProps = useWorkspace<ICustomerContact>(props, CustomerContact, {
    filters: [
      ...predefinedFilters,
      {
        filterOperation: FilterOperation.Equals,
        filterParameters: {
          field: 'customer.archived',
          value: false,
        },
      },
    ],
    columns: CUSTOMER_CONTACT_FIELD_NAMES,
    customColumns: customColumns,
    columnOverrides: CONTACT_DEFAULT_COLUMN_OVERRIDES,
    initialSortColumn: 'name',
  });

  return (
    <Workspace
      title={title}
      entityType={type}
      defaultQuery={workspace}
      {...workspaceProps}
      {...props}
      entity={CustomerContact}
      workspaceActionButtonsHOC={CustomerContactWorkspaceActions}
    />
  );
};

export default CustomerContactWorkspace;
